import { useTranslation } from 'module/hooks/use-translation';
import { NodeViewContent } from '@tiptap/react';
import {
  CustomSectionState,
  CustomSectionType,
  ExtractInfoCustomSectionAttributes,
} from 'api/custom-section/types';
import { useState, useCallback } from 'react';
import { ExtractInfoPromptType } from 'components/organisms/document-editor/extensions/custom-section/types';
import {
  SectionPreview,
  SectionTitle,
  SectionWrapper,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { PromptInput } from './prompt-input';
import { defaultCustomSectionNodeConfig } from '../../../../utils';
import { ExtractInfoSectionComponentProps } from '../types';

const choicesAsOption = (choice: string, index: number) => {
  return {
    label: choice,
    position: index + 1,
    value: index,
  };
};

export const ExtractInfoPromptSection = ({
  node,
  onRemove,
  onDone,
  onUndo,
  updateContent,
  contentEditable,
  allowResolve,
  onOpen,
  pageType,
  pageTriggerListFilter,
}: ExtractInfoSectionComponentProps) => {
  const showPrompt = node.attrs.state === CustomSectionState.Open;

  const constructPrompt = useCallback(
    (attributes: ExtractInfoCustomSectionAttributes) => {
      const payload = {
        ...defaultCustomSectionNodeConfig[CustomSectionType.ExtractInfo].config,
        ...attributes.config,
      };

      return {
        title: attributes.title,
        aiContextChecked: payload.aiContext ? true : false,
        aiContext: payload.aiContext,
        choices: {
          answerType: payload.choices.answerType,
          choices: payload.choices.choices.map(choicesAsOption),
          generateNewChoices: payload.choices.generateNewChoices,
        },
      };
    },
    [],
  );
  const [prompt, setPrompt] = useState<ExtractInfoPromptType>(
    constructPrompt(node.attrs),
  );

  const defaultTitleLabel = useTranslation(
    'rich_text_editor.sections.question_section.title',
  );

  const onGenerate = async (userPrompt: ExtractInfoPromptType) => {
    const attributes = {
      ...node.attrs,
      title: userPrompt.title,
      config: {
        aiContext: userPrompt.aiContext,
        choices: {
          answerType: userPrompt.choices.answerType,
          choices: userPrompt.choices?.choices?.map((choice) => choice.label),
          generateNewChoices: userPrompt.choices?.generateNewChoices,
        },
      },
      state: CustomSectionState.Closed,
    };

    onDone(attributes).then(() => {
      updateContent(
        [
          {
            type: 'RL_PLACEHOLDER_CONFIG',
            attrs: {
              icon: '',
              iconPosition: 'left',
              disabled: true,
            },
            content: [{ type: 'text', text: 'Answer' }],
          },
        ],
        attributes,
      );
    });
  };

  const onClose = useCallback(() => {
    if (node.content?.size === 0) {
      onRemove();

      return;
    }
    setPrompt(constructPrompt(node.attrs));
    onUndo();
  }, [onUndo, node.attrs, constructPrompt, node.content?.size, onRemove]);

  return (
    <div>
      {showPrompt && contentEditable ? (
        <PromptInput
          prompt={prompt}
          setPrompt={setPrompt}
          onGenerate={onGenerate}
          onCancel={() => {
            onRemove();
          }}
          onUndo={onClose}
          allowResolve={allowResolve}
          pageType={pageType}
          pageTriggerListFilter={pageTriggerListFilter}
        />
      ) : (
        <SectionPreview
          onClick={() => {
            onOpen();
          }}
        >
          <SectionWrapper>
            <SectionTitle>{prompt.title || defaultTitleLabel}</SectionTitle>

            <NodeViewContent />
          </SectionWrapper>
        </SectionPreview>
      )}
    </div>
  );
};
